import { Vec3, clamp } from './math.js';
import { Track, MAIN_SPLINE } from './track.js';
import { ShipState, InputFrame } from './physics.js';
import { makeFrame } from './spline.js';
import { Rng } from './rng.js';

export type BotDifficulty = 'cruise' | 'standard' | 'hostile' | 'nemesis';

export const BOT_DIFFICULTIES: BotDifficulty[] = ['cruise', 'standard', 'hostile', 'nemesis'];

interface DifficultyProfile {
  /** Multiplier on racing-line target speed. */
  speedScale: number;
  /** Max throttle. */
  throttleCap: number;
  /** Steering noise magnitude. */
  noise: number;
  /** Seconds of lookahead for the steering target. */
  lookahead: number;
  /** Willingness to boost (0..1). */
  boostAggression: number;
  /** How strongly they fight for the inside line vs yield. */
  contesting: number;
}

const PROFILES: Record<BotDifficulty, DifficultyProfile> = {
  cruise: { speedScale: 0.68, throttleCap: 0.82, noise: 0.09, lookahead: 0.55, boostAggression: 0.15, contesting: 0.2 },
  standard: { speedScale: 0.8, throttleCap: 0.92, noise: 0.05, lookahead: 0.6, boostAggression: 0.45, contesting: 0.5 },
  hostile: { speedScale: 0.9, throttleCap: 1.0, noise: 0.025, lookahead: 0.65, boostAggression: 0.7, contesting: 0.8 },
  nemesis: { speedScale: 0.985, throttleCap: 1.0, noise: 0.008, lookahead: 0.7, boostAggression: 0.92, contesting: 1.0 },
};

export const BOT_CALLSIGNS = [
  'VEKTOR', 'HALCYON-9', 'SABLE', 'MIRAGE', 'KILOWATT', 'NOMAD', 'PALEFIRE', 'RIFT',
  'CENTURION', 'AZIMUTH', 'QUARREL', 'STATIC', 'LUMEN', 'PIKE', 'OKTAV', 'REMORA',
  'THRESHER', 'GLASSJAW', 'MERIDIAN', 'HUSK',
];

/**
 * A bot is a controller: it reads the world like a player watches the screen and
 * writes an InputFrame. It obeys exactly the same physics as everyone else.
 */
export class BotController {
  private rng: Rng;
  private noisePhase: number;
  private stuckTimer = 0;
  private avoidBias = 0;
  private personality: { lineBias: number; brakePoint: number; aggression: number };

  constructor(
    seed: number,
    public difficulty: BotDifficulty
  ) {
    this.rng = new Rng(seed);
    this.noisePhase = this.rng.range(0, 1000);
    this.personality = {
      lineBias: this.rng.range(-1.6, 1.6),
      brakePoint: this.rng.range(0.92, 1.08),
      aggression: this.rng.range(0.75, 1.25),
    };
  }

  private frame = makeFrame();
  private target = new Vec3();
  private rel = new Vec3();

  drive(ship: ShipState, others: ShipState[], track: Track, tick: number, out: InputFrame): void {
    const prof = PROFILES[this.difficulty];
    const spline = track.spline(ship.splineId);
    const line = track.racingLine;
    const speed = ship.speed();

    // ── stuck recovery ──
    if (speed < 4 && ship.respawnTimer <= 0) {
      this.stuckTimer += 1 / 60;
    } else {
      this.stuckTimer = Math.max(0, this.stuckTimer - 1 / 30);
    }
    if (this.stuckTimer > 2.5) {
      out.respawn = true;
      this.stuckTimer = 0;
      return;
    }
    out.respawn = false;

    // ── steering target: point on the racing line ahead of us ──
    const lookDist = Math.max(18, speed * prof.lookahead);
    const targetS = spline.wrapS(ship.s + lookDist);
    spline.frameAt(targetS, this.frame);

    let desiredLateral = 0;
    if (ship.splineId === MAIN_SPLINE) {
      const li = Math.floor(targetS / spline.spacing) % line.offset.length;
      desiredLateral = line.offset[li] + this.personality.lineBias;
    }

    // ── overtaking / avoidance: shift line away from a ship blocking ahead ──
    this.avoidBias *= 0.92;
    for (const other of others) {
      if (other === ship || other.eliminated) continue;
      this.rel.copy(other.pos).sub(ship.pos);
      const ahead = this.rel.dot(ship.forward);
      if (ahead < 2 || ahead > 42) continue;
      this.rel.addScaled(ship.forward, -ahead);
      const side = this.rel.length();
      if (side < 6.5) {
        const sideSign = this.rel.dot(this.frame.right) >= 0 ? 1 : -1;
        // prefer diving to whichever side has room; contest inside lines when aggressive
        const room = this.frame.width / 2 - Math.abs(other.lateral);
        const dive = (room > 6 ? -sideSign : sideSign) * (5.5 - side * 0.5);
        this.avoidBias = clamp(this.avoidBias + dive * 0.25 * prof.contesting * this.personality.aggression, -8, 8);
      }
    }
    desiredLateral = clamp(
      desiredLateral + this.avoidBias,
      -this.frame.width / 2 + 3,
      this.frame.width / 2 - 3
    );

    this.target.copy(this.frame.pos).addScaled(this.frame.right, desiredLateral);

    // steer toward target point
    this.rel.copy(this.target).sub(ship.pos);
    const fwd = this.rel.dot(ship.forward);
    const rightVec = this.rel.dot(rightOf(ship));
    let steer = Math.atan2(rightVec, Math.max(1, fwd)) * 2.2;

    // steering noise (human wobble)
    const t = (tick + this.noisePhase) * 0.018;
    steer += Math.sin(t * 2.13) * Math.sin(t * 0.71) * prof.noise * 2;
    out.steer = clamp(steer, -1, 1);

    // ── throttle / braking from the speed plan ──
    let targetSpeed = 999;
    if (ship.splineId === MAIN_SPLINE) {
      // look at speed plan slightly further out, scaled by difficulty
      const brakeLook = Math.max(24, speed * 0.85 * this.personality.brakePoint);
      const si = Math.floor(spline.wrapS(ship.s + brakeLook) / spline.spacing) % line.speed.length;
      const siNow = Math.floor(ship.s / spline.spacing) % line.speed.length;
      targetSpeed = Math.min(line.speed[si], line.speed[siNow] * 1.12) * prof.speedScale;
    }

    if (speed > targetSpeed * 1.06) {
      out.throttle = 0.15;
      // airbrake into the corner, biased to the turning side
      const ab = clamp((speed - targetSpeed) / 26, 0.2, 1);
      out.brakeL = out.steer < -0.08 ? ab : ab * 0.25;
      out.brakeR = out.steer > 0.08 ? ab : ab * 0.25;
    } else {
      out.throttle = prof.throttleCap;
      out.brakeL = 0;
      out.brakeR = 0;
    }

    // ── boost strategy: straights, drafting, or desperate catch-up ──
    const straightAhead = targetSpeed > 900 || targetSpeed > speed + 22;
    const wantBoost =
      ship.boostEnergy > 0.35 &&
      straightAhead &&
      ship.grounded &&
      this.rng.next() < prof.boostAggression * 0.12;
    out.boost = out.boost ? ship.boostEnergy > 0.06 && straightAhead : wantBoost;

    // pitch: level out in the air
    out.pitch = ship.grounded ? 0 : clamp(-ship.forward.y * 1.4, -1, 1) * 0.5;
  }
}

const _right = new Vec3();
function rightOf(ship: ShipState): Vec3 {
  // TRUE pilot's right (forward × up) — must match the steering sign in physics.ts,
  // where steer > 0 turns toward this vector.
  return _right.cross(ship.forward, ship.up).normalize();
}
